import type { ReportQuery, ReportType } from '@pulsepoint/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get } from './client';
import type { Session } from './sessions';
import { sessionsSearch } from './sessions';

// ---- Reports

export interface ReportTotals {
  sessions: number;
  zeroSessions: number;
  energyWh: number;
  co2Kg: number;
  durationSec: number;
  duplicates: number;
}

interface Grouped {
  sessions: number;
  energyWh: number;
  co2Kg: number;
  durationSec: number;
}
export type SessionReportRow = Session & { possibleDuplicate: boolean };
/** Daily or Monthly by driver: one driver (or all pay-to-charge guests) in one day or month, with what it cost. */
export type DriverReportRow = Grouped & {
  /** "2026-10" or "2026-10-21". */
  period: string;
  driverId: string | null;
  driverName: string;
  organisationName: string | null;
  /** Their cards, e.g. "Work fob (04A1B2C3)". */
  cards: string | null;
  /** The "Public charging (guests)" row. */
  guests: boolean;
  /** At the private price (drivers) or the public price (guests), including Infinity EV's rate. */
  energyCostCents: number;
  idleFeeCents: number;
};
export type ChargerReportRow = Grouped & {
  chargerId: string;
  chargerName: string;
  chargePointId: string;
  bay: string | null;
  siteName: string | null;
  organisationName: string | null;
  organisationSiteCount: number;
  drivers: number;
  paidCents: number;
};
/**
 * Remittance: one organisation's month (each site's own month), in cents. Guests' payments less
 * refunds, card fees and Infinity EV's share, less Infinity EV's share of drivers' charging.
 */
export type RemittanceReportRow = Grouped & {
  organisationId: string;
  organisationName: string;
  /** The time zones of the sites, e.g. "Australia/Sydney". */
  timezones: string;
  publicSessions: number;
  privateSessions: number;
  publicPaidCents: number;
  refundCents: number;
  cardFeeCents: number;
  infinityevPublicCents: number;
  privateChargedCents: number;
  infinityevPrivateCents: number;
  /** Sessions whose price has no Infinity EV rate (prices set before the rate was kept). */
  noShare: number;
};
/** One charger's remittance, in one organisation's drill-down. */
export type RemittanceChargerRow = RemittanceReportRow & { chargerId: string; chargerName: string; siteName: string | null; organisationSiteCount: number };
/** One session's remittance, in one organisation's drill-down; money in cents. */
export type RemittanceSessionRow = Session & {
  /** The site's time zone, which the session is counted in. */
  timezone: string;
  pricePerKwh: number | null;
  infinityevRatePerKwh: number | null;
  /** What the guest paid (energy and idle fee), or what the driver was charged. */
  chargedCents: number;
  refundCents: number;
  cardFeeCents: number;
  infinityevCents: number;
  noShare: boolean;
};
/** A guest's session: what they paid less refund, card fee and Infinity EV; a driver's: less Infinity EV. */
export const sessionRemittanceCents = (s: RemittanceSessionRow) =>
  s.billingType === 'public' ? s.chargedCents - s.refundCents - s.cardFeeCents - s.infinityevCents : -s.infinityevCents;
export const remittanceCents = (r: RemittanceReportRow) => r.publicPaidCents - r.refundCents - r.cardFeeCents - r.infinityevPublicCents - r.infinityevPrivateCents;

export interface ReportRows {
  sessions: SessionReportRow;
  daily: DriverReportRow;
  drivers: DriverReportRow;
  chargers: ChargerReportRow;
  remittance: RemittanceReportRow;
}

export interface Report<T extends ReportType> {
  type: T;
  rows: ReportRows[T][];
  total: number;
  totals: ReportTotals;
  page: number;
  pageSize: number;
  /** Remittance with one organisation chosen: its chargers and sessions. */
  chargers?: RemittanceChargerRow[];
  sessions?: RemittanceSessionRow[];
}

export const reportsKey = ['reports'] as const;

export const useReport = <T extends ReportType>(type: T, q: ReportQuery, enabled = true) =>
  useQuery({
    queryKey: [...reportsKey, type, q],
    queryFn: () => get<Report<T>>(`/reports/${type}?${sessionsSearch(q as never)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const reportExportUrl = (type: ReportType, q: ReportQuery, format: 'xlsx' | 'csv') => {
  const { page: _p, pageSize: _s, ...rest } = q;
  return `/api/reports/${type}/export?${sessionsSearch({ ...rest, format } as never)}`;
};
